import {
  Alert,
  Button,
  Form,
  FormButton,
  showNotification,
  type FormControl,
  type FormSubmitHandler,
  type ValidationErrors,
} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';
import {useWatch} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {isEmpty, isNil} from 'ramda';

import {newVehicleApi} from '@dms/api/new-vehicle';
import {useTenant} from '@dms/shared';

import {isNotNilOrEmpty, type ApiError} from 'shared/web';

import {
  labelRequiredMessage,
  pricelistVersionSchema,
  type PricelistVersionValues,
} from '../schemas/pricelistVersionSchema';
import type {PricedItemChange} from '../types/PricedItemChange';
import {
  catalogueErrorCodes,
  catalogueErrorMessages,
  catalogueValidationErrors,
  isStaleCatalogueVersion,
  t,
} from '../utils/catalogueUi';
import {nextPricelistVersionLabel} from '../utils/nextPricelistVersionLabel';
import type {PricedItemChangeRequest} from './NewPricelistVersionDialog';
import {PriceChangesTable} from './PriceChangesTable';
import {PricelistVersionFields} from './PricelistVersionFields';

const DUPLICATE_PRICELIST_LABEL = 'DMS-NV-CT-0032';
const PRICELIST_VERSION_SCHEDULED = 'DMS-NV-CT-0066';
/** A single write lists its rows without a heading. */
const singleGroup = (
  items: PricedItemChange[]
): {key: string; title?: string; items: PricedItemChange[]} => ({key: 'items', items});
/**
 * Schema violations of the version payload, by the form field that holds the value. A violation of
 * the whole version comes for a missing or empty label; its raw schema text gives way to the
 * label's own message.
 */
const VERSION_FIELDS: {
  field: RegExp;
  name: keyof PricelistVersionValues;
  message?: () => string;
}[] = [
  {field: /pricelistVersion\W*label$/, name: 'nextVersion'},
  {field: /pricelistVersion\W*note$/, name: 'note'},
  {field: /(^|\W)pricelistVersion$/, name: 'nextVersion', message: labelRequiredMessage},
];

const versionFieldErrors = (error: ApiError): ValidationErrors =>
  catalogueValidationErrors(error).flatMap(({field, message}) =>
    VERSION_FIELDS.filter((version) => version.field.test(field)).map((version) => ({
      name: version.name,
      message: version.message?.() ?? message,
    }))
  );

/**
 * The confirm of a create, archive or inclusion switch on a published model year: the prices it
 * adds or takes away (computed on the client), the label and note of the version it creates. The
 * write waits for Confirm. A refused label and a scheduled version keep the dialog open; any other
 * refusal closes it and goes back to the caller.
 */
export function PricedItemChangeForm(props: {
  change: PricedItemChangeRequest;
  isApplying: boolean;
  onApplyingChange: (isApplying: boolean) => void;
  onCancel: () => void;
}) {
  const dispatch = useDispatch();
  const {tenantCurrency} = useTenant();
  const [refusal, setRefusal] = useState<string>();
  const isApplying = useRef(false);

  const fail = (error: ApiError) => {
    if (isStaleCatalogueVersion(error)) {
      dispatch(
        newVehicleApi.util.invalidateTags(['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'])
      );
    }
    props.change.onFailed(error);
  };
  const handleRefusal = (error: ApiError, setErrors: (errors: ValidationErrors | null) => void) => {
    const codes = catalogueErrorCodes(error);
    const fieldErrors = versionFieldErrors(error);
    const entryFailure = props.change.failureMessage?.(error);
    match(codes)
      .when(
        () => codes.includes(DUPLICATE_PRICELIST_LABEL),
        () => setErrors([{name: 'nextVersion', message: catalogueErrorMessages(error).join('\n')}])
      )
      .when(
        () => codes.includes(PRICELIST_VERSION_SCHEDULED),
        () => setRefusal(catalogueErrorMessages(error).join('\n'))
      )
      .when(
        () => !isNil(entryFailure),
        () => setRefusal(entryFailure)
      )
      .when(
        () => isEmpty(codes) && !isEmpty(fieldErrors),
        () => setErrors(fieldErrors)
      )
      .otherwise(() => fail(error));
  };
  const confirm: FormSubmitHandler<PricelistVersionValues> = async (values, setErrors) => {
    if (isApplying.current) {
      return;
    }
    isApplying.current = true;
    props.onApplyingChange(true);
    setRefusal(undefined);
    const label = values.nextVersion.trim();
    await props.change
      .submit({label, note: values.note.trim() || undefined})
      .then(() => {
        showNotification.success(
          t('entity.newVehicle.catalogue.itemChange.applied', 'Saved. Pricelist is now {label}.', {
            label,
          })
        );
        props.change.onWritten();
      })
      .catch((error: ApiError) => handleRefusal(error, setErrors))
      .finally(() => {
        isApplying.current = false;
        props.onApplyingChange(false);
      });
  };

  return (
    <Form<PricelistVersionValues>
      experimentalZodSchema={pricelistVersionSchema}
      // A label is checked as it is typed, so a disabled Confirm always shows why on the field,
      // and the field error is the only feedback: no generic warning on a refused submit.
      mode="onChange"
      shouldHideInvalidSubmitNotification
      defaultValues={{
        nextVersion: nextPricelistVersionLabel(props.change.modelYear.pricelistVersionLabel),
        note: '',
      }}
      onSubmit={confirm}
    >
      {(control) => (
        <VStack spacing={4}>
          <Text size="small">
            {t(
              'entity.newVehicle.catalogue.itemChange.hint',
              'This model year is published, so adding or removing a priced item gets its own pricelist version and a History entry.'
            )}
          </Text>
          <Text size="small" alternative>
            {t(
              'entity.newVehicle.catalogue.itemChange.unpublishHint',
              'Before the first configuration, you can also unpublish the model year, make the change and publish it again.'
            )}
          </Text>
          <VStack spacing={2}>
            <Text size="small" alternative>
              {t(
                'entity.newVehicle.catalogue.adjustment.previewTitle',
                'Prices that will change: {count}',
                {count: props.change.items.length}
              )}
            </Text>
            <Box maxHeight={80} overflowY="auto">
              <VStack spacing={3}>
                {(props.change.groups ?? [singleGroup(props.change.items)]).map((group) => (
                  <VStack key={group.key} spacing={1}>
                    <Show when={isNotNilOrEmpty(group.title) ? group.title : null}>
                      {(title) => (
                        <HStack justify="space-between" align="center" spacing={2}>
                          <Text size="small" alternative>
                            {title}
                          </Text>
                          <Show when={props.change.onRemoveEntry}>
                            {(removeEntry) => (
                              <Button
                                variant="link"
                                size="small"
                                title={t(
                                  'entity.newVehicle.catalogue.itemGroup.removeEntry',
                                  'Remove from group'
                                )}
                                isDisabled={props.isApplying}
                                onClick={() => {
                                  // A refusal named a change of the batch that Confirm sent; the
                                  // batch is another one now.
                                  setRefusal(undefined);
                                  removeEntry(group.key);
                                }}
                              />
                            )}
                          </Show>
                        </HStack>
                      )}
                    </Show>
                    <PriceChangesTable
                      currency={tenantCurrency}
                      modelYearId={props.change.modelYear.id}
                      changes={group.items}
                    />
                  </VStack>
                ))}
              </VStack>
            </Box>
          </VStack>
          <PricelistVersionFields
            control={control}
            currentLabel={props.change.modelYear.pricelistVersionLabel}
          />
          <Show when={refusal}>{(message) => <Alert variant="error" message={message} />}</Show>
          <HStack justify="flex-end" spacing={2}>
            <Button
              variant="secondary"
              title={t('general.actions.cancel', 'Cancel')}
              isDisabled={props.isApplying}
              onClick={props.onCancel}
            />
            <ConfirmButton control={control} hasItems={props.change.items.length > 0} />
          </HStack>
        </VStack>
      )}
    </Form>
  );
}

function ConfirmButton(props: {control: FormControl<PricelistVersionValues>; hasItems: boolean}) {
  // A cleared text field holds null.
  const label: string | null = useWatch({control: props.control, name: 'nextVersion'});
  return (
    <FormButton
      control={props.control}
      type="submit"
      isDisabled={!props.hasItems || !(label ?? '').trim()}
      title={t('general.actions.confirm', 'Confirm')}
    />
  );
}
